import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import ForDevelopersSection from '../components/ForDevelopersSection';
import CTASection from '../components/CTASection';
import { 
  Eye,
  Key,
  Percent,
  ArrowRight,
  CheckCircle,
  Buildings,
  Users,
  ShieldCheck,
  FileText,
  Handshake,
  ChartLineUp,
  Warning,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

const HowItWorksPage = () => {
  const navigate = useNavigate();
  const { openAuthModal } = useAuth();

  // Investor journey steps with images - new titles
  const investorSteps = [
    {
      number: '1.',
      title: 'Find your project.',
      description: 'Explore curated projects and understand the property, investment strategy, expected returns and associated risks.',
      details: [
        'Browse by asset type, location, and yield',
        'Review complete project documentation',
        'Analyze financial projections and risks',
        'Understand the investment thesis',
      ],
      image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/efdlx0qr_12FF5BE8-1A01-497D-A2D6-DE867DCDECD8.png',
    },
    {
      number: '2.',
      title: 'Own a fraction.',
      description: 'Invest from €100 and receive the appropriate digital representation of your participation in the investment.',
      details: [
        'Start with as little as €100',
        'Secure online investment process',
        'Sign documents digitally',
        'Receive instant confirmation',
      ],
      image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/mdk8n85r_08B62DFC-0CD2-464F-AEDB-527FA6BD4B85.png',
    },
    {
      number: '3.',
      title: 'Reap the earnings.',
      description: 'Participate in the economics of the investment without managing the property yourself.',
      details: [
        'Receive rental income distributions',
        'Participate in capital appreciation',
        'Track performance in real-time',
        'Tax documentation provided',
      ],
      image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/0xzxe5h9_81803B5E-2EFA-4CFC-B5EF-490E180D51D2.png',
    },
  ];

  // Developer journey steps
  const developerSteps = [
    {
      number: '01',
      icon: FileText,
      title: 'Submit Your Project',
      description: 'Tell us about your real estate project. Provide details about the property, financing needs, and projected returns.',
    },
    {
      number: '02',
      icon: ShieldCheck,
      title: 'Due Diligence Review',
      description: 'Our team conducts thorough due diligence on your project, including legal, financial, and market analysis.',
    },
    {
      number: '03',
      icon: Buildings,
      title: 'Project Goes Live',
      description: 'Once approved, your project is listed on our marketplace and made available to our investor community.',
    },
    {
      number: '04',
      icon: Handshake,
      title: 'Manage & Distribute',
      description: 'We handle investor relations, reporting, and distribution management throughout the project lifecycle.',
    },
  ];

  return (
    <div className="min-h-screen bg-[rgb(var(--background))]" data-testid="how-it-works-page">
      <Header />

      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        {/* Background Image - Guy on phone */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url("https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/lbh3jtr9_ChatGPT%20Image%20Sep%207%2C%202026%20at%2004_29_05%20PM.png")'
          }}
        />
        
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-slate-900/70" />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
        
        <div className="container mx-auto px-6 md:px-12 lg:px-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm font-medium text-primary tracking-[0.3em] uppercase mb-6">
              How It Works
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-medium text-white mb-4 tracking-tight">
              Being a landlord.
            </h1>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-medium text-white/40 mb-8 tracking-tight">
              The easy, smart and digital way.
            </h1>
            <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed">
              Invest in curated real-estate projects without purchasing or managing the entire property. 
              We handle the complexity so you can focus on growing your wealth.
            </p>
          </div>
        </div>
      </section>

      {/* For Investors Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="investor-journey">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              <Users size={18} weight="duotone" />
              For Investors
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-medium mb-4 tracking-tight">
              Three steps. <span className="text-primary">That&apos;s it.</span>
            </h2>
            <p className="text-lg text-foreground-muted max-w-2xl mx-auto">
              We&apos;ve removed the complexity. Here&apos;s how you get into real estate.
            </p>
          </div>

          <div className="space-y-16 lg:space-y-24">
            {investorSteps.map((step, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <h3 className="text-3xl md:text-4xl font-heading font-medium mb-4 tracking-tight">
                    <span className="text-primary">{step.number}</span> {step.title}
                  </h3>
                  <p className="text-lg text-foreground-muted mb-6 leading-relaxed">
                    {step.description}
                  </p>
                  <ul className="space-y-3">
                    {step.details.map((detail, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <CheckCircle className="text-primary flex-shrink-0" size={20} weight="fill" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="flex items-center justify-center" style={{ perspective: '1000px' }}>
                    <motion.img 
                      src={step.image} 
                      alt={step.title}
                      className="w-full max-w-[330px] h-auto object-contain"
                      animate={{ 
                        rotateY: [-10, 10, -10]
                      }}
                      transition={{ 
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Button
              size="lg"
              className="bg-primary hover:bg-primary-hover text-white rounded-full px-10 py-7 text-lg font-medium transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/25"
              onClick={() => navigate('/projects')}
            >
              Explore projects <ArrowRight className="ml-2" size={20} weight="bold" />
            </Button>
          </div>
        </div>
      </section>

      {/* Risk Disclaimer Section */}
      <section className="py-16 bg-slate-900">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8 max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <Warning className="text-amber-400 flex-shrink-0 mt-1" size={24} weight="duotone" />
              <div>
                <h4 className="font-semibold text-white mb-2">Important Information About Risk</h4>
                <p className="text-white/70 text-sm leading-relaxed">
                  All investments carry risk. The value of your investment can go down as well as up, 
                  and you may receive back less than you invest. Past performance is not a reliable 
                  indicator of future results. Real estate investments are illiquid and should be 
                  considered long-term. Returns are not guaranteed. Please read all documentation 
                  carefully before investing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Developers Section */}
      <ForDevelopersSection />

      {/* Benefits Section */}
      <section className="py-20 md:py-28 bg-[rgb(var(--background))]">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Investor Benefits */}
            <div className="bg-white rounded-3xl p-8 lg:p-10 border border-black/[0.04]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Users size={16} weight="duotone" />
                Investor Benefits
              </div>
              <h3 className="text-2xl md:text-3xl font-heading font-medium mb-6 tracking-tight">
                There had to be a better way in.
              </h3>
              <ul className="space-y-4">
                {[
                  '€100 gets you through the door',
                  'Diversify across multiple projects',
                  'Professional due diligence on every project',
                  'Transparent fees and projected returns',
                  'Regular income distributions',
                  'Secondary market liquidity (coming soon)',
                ].map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={20} weight="fill" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Developer Benefits */}
            <div className="bg-slate-900 rounded-3xl p-8 lg:p-10 text-white">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-white text-sm font-medium mb-6">
                <Buildings size={16} weight="duotone" />
                Developer Benefits
              </div>
              <h3 className="text-2xl md:text-3xl font-heading font-medium mb-6 tracking-tight">
                A new source of capital.
              </h3>
              <ul className="space-y-4">
                {[
                  'Access to a network of retail investors',
                  'Digital-first fundraising process',
                  'Automated investor communications',
                  'Compliance and legal support',
                  'Marketing exposure to qualified investors',
                  'Ongoing investor relations management',
                ].map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={20} weight="fill" />
                    <span className="text-white/90">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />

      <Footer />
    </div>
  );
};

export default HowItWorksPage;
